import React, { useState } from "react";
import { LifeBuoy, Power } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "./ssoFormat";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

function EnableDialog({ open, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent data-testid="breakglass-enable-dialog">
        <DialogHeader>
          <DialogTitle>Switch on emergency access?</DialogTitle>
          <DialogDescription>
            Dedicated emergency identities will be able to sign in from the emergency page for
            15-minute sessions. Every administrator is emailed and every attempt is recorded.
            Ordinary sign-in is not affected.
          </DialogDescription>
        </DialogHeader>
        <label className="block my-4">
          <span className="field-label">Incident reason</span>
          <textarea className="input-flat" rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Microsoft Entra sign-in outage since 09:10" data-testid="breakglass-reason" />
        </label>
        <DialogFooter>
          <button className="btn-ghost" onClick={onClose} data-testid="breakglass-enable-cancel">Cancel</button>
          <button className="btn-primary" disabled={reason.trim().length < 8} onClick={() => { onSubmit(reason.trim()); setReason(""); }} data-testid="breakglass-enable-continue">Continue</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BreakglassCard({ data, onStepUp, onChanged }) {
  const [enabling, setEnabling] = useState(false);
  const { state, active_sessions: sessions, identities, viewer_is_emergency: isEmergency } = data;
  const hasActive = identities.some((identity) => identity.active);

  const enable = (reason) => {
    setEnabling(false);
    onStepUp({
      purpose: "breakglass", title: "Confirm emergency access",
      onConfirm: async (grant) => {
        await api.post("/admin/breakglass/enable", { reason, grant });
        toast.warning("Emergency access is on");
        onChanged();
      },
    });
  };
  const disable = async () => {
    if (!window.confirm("Switch off emergency access? Any emergency sessions end immediately. Ordinary administrator sessions are not affected.")) return;
    try {
      const { data: result } = await api.post("/admin/breakglass/disable");
      if (isEmergency) {
        toast.success("Emergency access is off and your emergency session has ended");
        setTimeout(() => window.location.assign("/login"), 1500);
        return;
      }
      toast.success(`Emergency access is off${result.sessions_ended ? `; ${result.sessions_ended} session(s) ended` : ""}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="border p-6" style={{ borderColor: state.enabled ? "rgba(138,90,0,0.5)" : "var(--border)", background: state.enabled ? "#FBF3E4" : "var(--surface)" }} data-testid="breakglass-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-1.5"><LifeBuoy size={11} /> Breakglass</div>
          <h3 className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>Emergency administrator access</h3>
        </div>
        <span className={`pill ${state.enabled ? "pill-warn" : ""}`} data-testid="breakglass-state">{state.enabled ? "On" : "Off"}</span>
      </div>
      {state.enabled ? (
        <p className="text-sm mb-4" style={{ color: "#8A5A00" }} data-testid="breakglass-enabled-detail">
          Switched on {formatWhen(state.enabled_at)} by {state.enabled_by}. Reason: {state.reason}
        </p>
      ) : (
        <p className="text-xs mb-4 max-w-xl" style={{ color: "var(--muted)" }}>
          Off by default. Switch it on only during a Microsoft 365 outage. Emergency identities then
          use the subtle “Emergency administrator access” link beneath the Microsoft button.
          {state.disabled_at && ` Last switched off ${formatWhen(state.disabled_at)} by ${state.disabled_by}.`}
        </p>
      )}
      {sessions.length > 0 && (
        <div className="mb-4 text-xs" data-testid="breakglass-sessions">
          {sessions.map((session) => (
            <div key={session.started_at} className="py-1" style={{ color: "var(--ink)" }}>
              {session.identity} · started {formatWhen(session.started_at)} · ends {formatWhen(session.expires_at)} · {session.ip}
            </div>
          ))}
        </div>
      )}
      {state.enabled ? (
        <button className="btn-primary flex items-center gap-2" onClick={disable} data-testid="breakglass-disable"><Power size={14} /> Switch off and end the incident</button>
      ) : (
        <>
          <button className="btn-primary flex items-center gap-2" disabled={!hasActive || isEmergency} onClick={() => setEnabling(true)} data-testid="breakglass-enable"><Power size={14} /> Switch on emergency access</button>
          {!hasActive && <p className="text-xs mt-2" style={{ color: "var(--muted)" }} data-testid="breakglass-needs-identity">Create an active emergency identity first.</p>}
        </>
      )}
      <EnableDialog open={enabling} onClose={() => setEnabling(false)} onSubmit={enable} />
    </section>
  );
}
